import { CalendarClock, ExternalLink, Send, Sparkles, Undo2 } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { SocialPost, SocialProvider } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fillFromProduct, fromSocialPost } from '../api/post-mappers';
import { usePostProductMedia } from '../hooks/use-post-product-media';
import { usePostProducts } from '../hooks/use-post-products';
import {
  PINTEREST_LIMITS,
  postFormSchema,
  type PostFormInput,
  type PostFormOutput,
} from '../schemas/post-form-schema';
import { canPublishPost, publishBlockers } from '../schemas/post-state';

import { BoardPicker } from './board-picker';
import { TextCounter } from './counter-field';
import { MediaStrip } from './media-strip';
import { PostPreview } from './post-preview';
import { PostProductsField } from './post-products-field';
import { PostStatusBadge } from './post-status-badge';
import { ScheduleDialog } from './schedule-dialog';

export const POST_FORM_ID = 'social-post-form';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'productIds',
  'variantIds',
  'mediaIds',
  'target',
  'title',
  'description',
  'altText',
  'link',
]);
// `mediaIds.2` or `target.boardId` from the backend lands on the field as a whole.
const mapField = (field: string) => {
  const head = field.split('.')[0] ?? field;
  return head === 'variantIds' ? 'variants' : head;
};

const TEXT_FIELDS = [
  { name: 'title', max: PINTEREST_LIMITS.title, multiline: false },
  { name: 'description', max: PINTEREST_LIMITS.description, multiline: true },
  { name: 'altText', max: PINTEREST_LIMITS.altText, multiline: true },
  { name: 'link', max: PINTEREST_LIMITS.link, multiline: false },
] as const;

export interface PostFormProps {
  storeId: string;
  provider: SocialProvider;
  /** The store's IANA zone, for the schedule dialog. */
  timeZone: string;
  /** `https://{primaryDomain}` for "Fill from product"; null leaves the link alone. */
  baseUrl: string | null;
  /** The saved post in edit mode. */
  post: SocialPost | null;
  defaultValues: PostFormInput;
  /** False opens the form read-only: a locked status, a closed store or no `social.write`. */
  editable: boolean;
  /** Why the form is read-only, shown above it. */
  readOnlyNotice?: ReactNode;
  saving: boolean;
  publishing: boolean;
  /** Creates or updates; resolves with the saved post. */
  onSubmit: (values: PostFormOutput) => Promise<SocialPost>;
  /** After a Save from the bar; the create page moves to the new post's page. */
  onSaved?: (post: SocialPost) => void;
  /** Publish now (no time) or schedule; rejections are rendered here. */
  onPublish: (post: SocialPost, publishAt: string | undefined) => Promise<unknown>;
  onUnschedule?: () => Promise<unknown>;
  dangerZone?: ReactNode;
}

/**
 * The post editor: products → images (1–5, reorder) → board → text with counters, the 2:3
 * preview and the publish card in the aside, and the contextual save bar for Save. Publishing
 * saves first when the text is dirty; a new image list must be saved and rendered before.
 */
export function PostForm({
  storeId,
  provider,
  timeZone,
  baseUrl,
  post,
  defaultValues,
  editable,
  readOnlyNotice,
  saving,
  publishing,
  onSubmit,
  onSaved,
  onPublish,
  onUnschedule,
  dangerZone,
}: PostFormProps) {
  const { t } = useTranslation(['social', 'common']);
  const message = useErrorMessage();
  const format = useFormat();
  const ids = useId();
  const network = t(`social:${provider}.name`);
  const form = useForm<PostFormInput, unknown, PostFormOutput>({
    resolver: useI18nResolver(postFormSchema),
    defaultValues,
  });
  const [publishError, setPublishError] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);

  const productIds = useWatch({ control: form.control, name: 'productIds' });
  const variants = useWatch({ control: form.control, name: 'variants' });
  const mediaIds = useWatch({ control: form.control, name: 'mediaIds' });
  const target = useWatch({ control: form.control, name: 'target' });
  const title = useWatch({ control: form.control, name: 'title' });
  const altText = useWatch({ control: form.control, name: 'altText' });

  const productMedia = usePostProductMedia(productIds);
  const primaryIds = useMemo(() => productIds.slice(0, 1), [productIds]);
  const [primary] = usePostProducts(primaryIds);
  const renditions = useMemo(
    () => new Map((post?.media ?? []).map((item) => [item.mediaId, item.url])),
    [post],
  );
  const slides = useMemo(
    () => mediaIds.map((mediaId) => ({ mediaId, url: renditions.get(mediaId) ?? null })),
    [mediaIds, renditions],
  );
  const blockers = publishBlockers({ target, mediaIds }, post, defaultValues.mediaIds);
  const canPublish = editable && blockers.length === 0 && (!post || canPublishPost(post.status));

  const save = async (): Promise<SocialPost | null> => {
    let saved: SocialPost | null = null;
    await form.handleSubmit(async (values) => {
      form.clearErrors('root.server');
      try {
        saved = await onSubmit(values);
        if (post) form.reset(fromSocialPost(saved));
      } catch (error) {
        if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    })();
    return saved;
  };
  const saveFromBar = async () => {
    const saved = await save();
    if (saved) onSaved?.(saved);
  };

  useSaveBar({ form, onSave: saveFromBar, saving, disabled: !editable });

  const publish = async (publishAt: string | undefined) => {
    setPublishError(null);
    let saved = post;
    if (!saved || form.formState.isDirty) {
      saved = await save();
      if (!saved) return;
    }
    try {
      await onPublish(saved, publishAt);
    } catch (error) {
      setPublishError(message(error));
      throw error;
    }
  };

  const fill = () => {
    const product = primary?.product;
    if (!product) return;
    const variant = variants.find((v) => v.productId === product.id)?.variantId ?? null;
    const filled = fillFromProduct(product, baseUrl, variant);
    const set = (name: 'title' | 'description' | 'altText' | 'link', value: string) =>
      form.setValue(name, value, { shouldDirty: true, shouldValidate: true });
    set('title', filled.title);
    set('description', filled.description);
    set('altText', filled.altText);
    if (filled.link) set('link', filled.link);
  };

  const aside = (
    <div className="flex flex-col gap-4">
      <PostPreview
        slides={slides}
        lookup={productMedia.lookup}
        title={title}
        boardName={target?.boardName ?? null}
        altText={altText}
        network={network}
      />
      <section
        aria-labelledby={`${ids}-publish`}
        className="bg-card text-card-foreground flex flex-col gap-3 rounded-xl border p-4"
      >
        <div className="flex items-center justify-between gap-2">
          <h3 id={`${ids}-publish`} className="text-sm font-semibold">
            {t('social:editor.publish.title')}
          </h3>
          {post ? <PostStatusBadge status={post.status} /> : null}
        </div>
        {post?.status === 'scheduled' && post.publishAt ? (
          <p className="text-sm" title={format.utc(post.publishAt)}>
            {t('social:editor.publish.scheduledFor', { when: format.dateTime(post.publishAt) })}
          </p>
        ) : null}
        {post?.status === 'published' && post.publishedAt ? (
          <p className="text-sm" title={format.utc(post.publishedAt)}>
            {t('social:editor.publish.publishedAt', { when: format.dateTime(post.publishedAt) })}
          </p>
        ) : null}
        {post?.externalUrl ? (
          <a
            href={post.externalUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary inline-flex items-center gap-1 text-sm underline-offset-4 hover:underline"
          >
            <ExternalLink className="size-4" aria-hidden />
            {t('social:editor.publish.openOnNetwork', { network })}
          </a>
        ) : null}
        {post?.status === 'failed' && post.lastError ? (
          <Alert variant="destructive" role="status">
            <AlertTitle>{t('social:editor.publish.failedTitle')}</AlertTitle>
            <AlertDescription>
              {post.lastError}
              {post.attempts > 0 ? (
                <span className="text-xs">
                  {t('social:editor.publish.attempts', { count: post.attempts })}
                </span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        {publishError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{publishError}</AlertDescription>
          </Alert>
        ) : null}
        {editable && (!post || canPublishPost(post.status)) ? (
          <>
            {blockers.length > 0 ? (
              <ul
                className="text-muted-foreground list-disc pl-4 text-xs"
                aria-label={t('social:editor.publish.blockersLabel')}
              >
                {blockers.map((blocker) => (
                  <li key={blocker}>{t(`social:editor.publish.blockers.${blocker}`)}</li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-xs">{t('social:editor.publish.ready')}</p>
            )}
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                disabled={!canPublish || publishing || saving}
                onClick={() => void publish(undefined).catch(() => undefined)}
              >
                <Send aria-hidden />
                {post?.status === 'failed'
                  ? t('social:editor.publish.retry')
                  : t('social:editor.publish.now')}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!canPublish || publishing || saving}
                onClick={() => setScheduling(true)}
              >
                <CalendarClock aria-hidden />
                {post?.status === 'scheduled'
                  ? t('social:editor.publish.reschedule')
                  : t('social:editor.publish.schedule')}
              </Button>
              {post?.status === 'scheduled' && onUnschedule ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={publishing || saving}
                  onClick={() => void onUnschedule()}
                >
                  <Undo2 aria-hidden />
                  {t('social:editor.publish.unschedule')}
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </section>
    </div>
  );

  return (
    <Form {...form}>
      <FormLayout aside={aside}>
        <form
          id={POST_FORM_ID}
          onSubmit={(event) => {
            event.preventDefault();
            void saveFromBar();
          }}
          noValidate
          className="flex flex-col gap-6"
        >
          {readOnlyNotice}
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}

          <FormSection
            title={t('social:editor.products.title')}
            description={t('social:editor.products.description')}
          >
            <FormField
              control={form.control}
              name="productIds"
              render={() => (
                <FormItem>
                  <PostProductsField
                    storeId={storeId}
                    productIds={productIds}
                    variants={variants}
                    disabled={!editable}
                    onChange={(next) => {
                      form.setValue('productIds', next.productIds, { shouldDirty: true });
                      form.setValue('variants', next.variants, { shouldDirty: true });
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            title={t('social:editor.media.title')}
            description={t('social:editor.media.description')}
          >
            <FormField
              control={form.control}
              name="mediaIds"
              render={({ field }) => (
                <FormItem>
                  <MediaStrip
                    value={field.value}
                    onChange={(ids) =>
                      form.setValue('mediaIds', ids, { shouldDirty: true, shouldValidate: true })
                    }
                    candidates={productMedia.candidates}
                    lookup={productMedia.lookup}
                    renditions={renditions}
                    loading={productMedia.isPending}
                    error={productMedia.error}
                    disabled={!editable}
                    noProducts={productIds.length === 0}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            title={t('social:editor.board.title', { network })}
            description={t('social:editor.board.description')}
          >
            <FormField
              control={form.control}
              name="target"
              render={({ field }) => (
                <FormItem>
                  <BoardPicker
                    storeId={storeId}
                    provider={provider}
                    value={field.value}
                    onChange={(next) =>
                      form.setValue('target', next, { shouldDirty: true, shouldValidate: true })
                    }
                    disabled={!editable}
                    boardId={`${ids}-board`}
                    sectionId={`${ids}-section`}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            title={t('social:editor.text.title')}
            description={t('social:editor.text.description', { network })}
          >
            {editable ? (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!primary?.product}
                  onClick={fill}
                >
                  <Sparkles aria-hidden />
                  {t('social:editor.text.fill')}
                </Button>
                <p className="text-muted-foreground mt-1 text-xs">
                  {primary?.product
                    ? t('social:editor.text.fillHint', { product: primary.product.title })
                    : t('social:editor.text.fillNoProduct')}
                </p>
              </div>
            ) : null}
            {TEXT_FIELDS.map(({ name, max, multiline }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>{t(`social:editor.text.${name}`)}</FormLabel>
                      <TextCounter
                        length={field.value.length}
                        max={max}
                        id={`${ids}-${name}-count`}
                      />
                    </div>
                    <FormControl>
                      {multiline ? (
                        <Textarea
                          {...field}
                          rows={name === 'description' ? 5 : 2}
                          disabled={!editable}
                          aria-describedby={`${ids}-${name}-count`}
                        />
                      ) : (
                        <Input
                          {...field}
                          type={name === 'link' ? 'url' : 'text'}
                          disabled={!editable}
                          aria-describedby={`${ids}-${name}-count`}
                        />
                      )}
                    </FormControl>
                    <FormDescription>{t(`social:editor.text.${name}Hint`)}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </FormSection>
          {dangerZone}
        </form>
      </FormLayout>
      <ScheduleDialog
        open={scheduling}
        onOpenChange={setScheduling}
        title={t('social:schedule.title')}
        description={t('social:schedule.description', { network })}
        timeZone={timeZone}
        initial={post?.status === 'scheduled' ? post.publishAt : null}
        submitting={publishing || saving}
        onSubmit={(publishAt) => publish(publishAt)}
      />
    </Form>
  );
}
